import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { CreateManualReceiptRequest } from "@/api/types";
import { ConflictError } from "@/api/client";
import { useTrip } from "@/hooks/useTrip";
import { useParticipants } from "@/hooks/useParticipants";
import { useCreateManualReceipt } from "@/hooks/useReceipts";
import { useShareTrip } from "@/hooks/useShareTrip";
import { PageShell } from "@/components/PageShell";
import { InTripHeader } from "@/components/InTripHeader";
import { Skeleton } from "@/components/Skeleton";
import { ManualReceiptForm } from "@/components/ManualReceiptForm";
import { UploadClosedCard } from "@/components/UploadClosedCard";
import { useToast } from "@/components/toast/ToastProvider";
import "./manual-receipt-create.css";

/**
 * Manual create screen (ADR-0031, US-20, wireframe §B). Composes
 * <ManualReceiptForm> under a slim <InTripHeader> whose meta line is hidden
 * (no receipt exists yet). On «Створити» the receipt is created (born
 * confirmed, reflected immediately), then we navigate(replace) to its detail
 * screen so a browser-back lands on the receipts list, never on a half-filled
 * form. A 409 trip_closed race swaps in the info <UploadClosedCard>.
 */
export function ManualReceiptCreateScreen() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const tripQ = useTrip(slug);
  const participantsQ = useParticipants(slug);
  const handleShare = useShareTrip(tripQ.data);
  const create = useCreateManualReceipt(slug);

  const [closed, setClosed] = useState(false);

  if (tripQ.isLoading || !tripQ.data) {
    return (
      <PageShell>
        <Skeleton variant="block" width="100%" height={56} />
        <Skeleton variant="block" width="100%" height={320} />
      </PageShell>
    );
  }

  const trip = tripQ.data;
  const participants = participantsQ.data?.participants ?? [];

  function handleCreate(draft: CreateManualReceiptRequest): void {
    create.mutate(draft, {
      onSuccess: (receipt) => {
        toast.push({ tone: "success", title: "Витрату додано" });
        // Replace, not push: back from detail returns to the receipts list,
        // never to a stale create form (same principle as receipt-upload).
        navigate(`/t/${trip.trip_id}/receipts/${receipt.receipt_id}`, {
          replace: true,
        });
      },
      onError: (err) => {
        if (err instanceof ConflictError && err.conflictReason === "trip_closed") {
          // Someone closed the trip while the form was open. Nothing was
          // saved (manual create touches no S3). Info card, no retry.
          setClosed(true);
          return;
        }
        toast.push({ tone: "error", title: "Не вдалося зберегти. Спробуй ще." });
      },
    });
  }

  return (
    <PageShell
      header={<InTripHeader trip={trip} onShare={handleShare} />}
    >
      <div className="wp-mrc">
        {closed ? (
          <UploadClosedCard onClose={() => navigate(`/t/${trip.trip_id}/receipts`)} />
        ) : (
          <ManualReceiptForm
            trip={trip}
            participants={participants}
            submitting={create.isPending}
            onCreate={handleCreate}
          />
        )}
      </div>
    </PageShell>
  );
}
